import { useMemo } from 'react'
import {
  getName,
  HOUR,
  isNotEmpty,
  isNotDefined,
  isRealUserId,
  type TinyUser,
  type UserGetInput,
  type UserGetSuccess,
  type UserIdentifier,
  type UserId,
} from '@terros/common'
import { useQueries, type UseQueryResult } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { tinyUserKey } from '../keys'
import { type GetHookResponse } from '../hooks'
import { useServerRequest } from '../api'
import { getPlaceholderData } from './util'
import { useUserCache } from './cache'

export const useTinyUser = (userId: UserId | undefined, enabled = true): GetHookResponse<TinyUser> => {
  const cache = useUserCache()
  const real = isRealUserId(userId)
  const placeholderData = real ? cache.getTinyUser(userId) : getPlaceholderData(userId)

  return useTantalimQuery<UserGetInput, UserGetSuccess, TinyUser>({
    url: '/user/get',
    queryKey: tinyUserKey(userId),
    input: {
      userId,
    },
    convertResponse: ({ user }) => {
      cache.setUser(user)
      return user
    },
    options: {
      enabled: real && enabled,
      gcTime: Infinity,
      staleTime: HOUR,
      placeholderData,
    },
  })
}

export const useTinyUserByIdentifier = (
  userId: UserIdentifier | undefined,
  enabled = true
): GetHookResponse<TinyUser> => {
  const cache = useUserCache()

  return useTantalimQuery<UserGetInput, UserGetSuccess, TinyUser>({
    url: '/user/get',
    queryKey: tinyUserKey(userId),
    input: {
      userId,
    },
    convertResponse: ({ user }) => {
      cache.setUser(user)
      return user
    },
    options: {
      enabled: isNotEmpty(userId) && enabled,
      gcTime: Infinity,
      staleTime: HOUR,
    },
  })
}

export const useTinyUsers = (userIds: UserId[]): UseQueryResult<TinyUser>[] => {
  const serverRequest = useServerRequest<UserGetInput, UserGetSuccess>('/user/get')
  const cache = useUserCache()
  return useQueries({
    queries: userIds.map((userId) => {
      const real = isRealUserId(userId)
      const placeholderData = real ? cache.getTinyUser(userId) : getPlaceholderData(userId)
      return {
        queryKey: tinyUserKey(userId),
        queryFn: async () => {
          const { user } = await serverRequest({ userId })
          cache.setUser(user)
          return user
        },
        staleTime: HOUR,
        gcTime: Infinity,
        enabled: real,
        placeholderData,
      }
    }),
  })
}

export function useUserName(userId: UserId | undefined): string | undefined {
  const { isLoading, error, data: user } = useTinyUser(userId)
  if (!userId) return
  if (isLoading) return 'Loading...'
  if (error) return `Error: ${error}`
  if (!user) return 'Not Found'
  return getName(user)
}

export const useFindTinyUser = ({
  userId,
  user,
}: {
  userId: UserId
  user: TinyUser | undefined
}): GetHookResponse<TinyUser> => {
  const { data, ...state } = useTinyUser(userId, isNotDefined(user))

  const tinyUser = useMemo(() => {
    if (isNotDefined(user)) return data
    return user
  }, [data, user])

  return { data: tinyUser, ...state }
}
